<template>
  <v-dialog v-if="project" id="dialog" v-model="dialog" :max-width="namespaceSelection ? '700px': '900px'" :overlay-opacity="0.93">          
    <template v-slot:activator="{ on }">
    <v-btn
      v-on="on"
      text 
      small
      class="primary--text"
      :disabled="project.applicationCount >= project.quota.applications"
    >
      <v-icon small class="mr-1">add_box</v-icon>
      New Application
    </v-btn>
    </template>
    <!-- Namespace selection if not selected -->
    <v-card v-if="namespaceSelection" class="pa-5">
      <v-card-title>Select namespace</v-card-title>
      <v-card-text>
        <v-container fluid v-if="namespaces.length > 0">
          <v-row align="center">
            <v-select
              :items="namespaces"
              label="Select a namespace"
              v-model="overrideNamespace"
              item-text="name"
              item-value="id"
              solo
            ></v-select>
          </v-row>
          <v-row>
            <p>
              Namespaces allow you to organize your applications and secrets. 
              Use them to separate development, testing and production environments.
            </p>  
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
    <v-card v-else class="pa-5">
      <v-tabs v-model="tab">
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-form">Config</v-tab>
        <v-tab href="#tab-yaml">YAML</v-tab>
      </v-tabs>


      <v-tabs-items v-model="tab" class="mt-4">
        <v-tab-item value="tab-form">
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
          >
            <v-card-text>            
              <v-row align="center" dense>
                <v-col class="d-flex" md="12" sm="12" xs="12">
                  <v-text-field
                    v-model="application.name"
                    :rules="nameRules"
                    :counter="32"
                    label="Application name"
                    required
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row align="center" dense>
                <v-col class="d-flex" md="12" sm="12" xs="12">
                  <v-text-field
                    v-model="application.image"
                    :rules="imageRules"
                    label="Container Image"
                    required
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row align="center" dense>
                <v-col class="d-flex" md="12" sm="12" xs="12">
                  <v-textarea
                    v-model="application.args"   
                    rows="3"               
                    label="Container Start Arguments"                  
                    hint="This provides arguments for the Docker container"
                    outlined
                  ></v-textarea>
                </v-col>
              </v-row>
              
              <v-row align="center" dense>
                <v-col  md="6" sm="12" xs="12">
                  <v-text-field
                    
                    v-model="application.ports"    
                    label="Expose TCP Ports"                  
                    hint="Comma separated list of ports, for example 80,443 or 8080:80"
                    outlined
                    ></v-text-field>
                </v-col>

                <v-col md="6" sm="12" xs="12">
                  <v-text-field
                    
                    v-model="application.volume"                        
                    label="Volume Mount"                  
                    hint="For example /data/my-app:/data"
                    :rules="volumeRules"
                    outlined
                    ></v-text-field>
                </v-col>
              </v-row>

              <template v-for="(env, i) in application.env">
                <v-row :key="`${application.name}-env-${i}`" dense>
                  <v-col md="6" sm="6" xs="6">
                    <v-text-field
                      dense
                      v-model="env.name"
                      :rules="envRules"
                      label="Key"
                      outlined
                    ></v-text-field>
                  </v-col>

                  <v-col sm="5">
                    <v-text-field
                      dense
                      v-model="env.value"
                      :rules="envRules"
                      label="Value"
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col sm="1">
                    <div class="text-center">
                      <v-btn
                        fab
                        icon
                        small
                        @click="deleteEnvVar(application, i)"
                      >
                        <v-icon class="red--text">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </template>
    
              <v-row align="center" dense>
                <v-col class="d-flex" md="12" sm="12" xs="12">
                  
                  <v-btn
                    outlined
                    class="secondary--text pr-2"
                    :disabled="loading"
                    :loading="loading"
                    small
                    @click="addEnvVar(application)"
                  >
                    <v-icon small class="mr-1">add</v-icon>
                    <span class="mr-1 pt-1 pb-1">Add Env Variable</span>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <p class="mt-1 secondary--text">
                    Add a plain text environment variable
                  </p>
                </v-col>
              </v-row>

              <template v-for="(env, i) in application.secretEnv">
                <v-row :key="`${application.name}-secret-env-${i}`" dense>
                  <v-col md="6" sm="6" xs="6">
                    <v-text-field
                      dense
                      v-model="env.name"
                      :rules="envRules"
                      label="Key"
                      outlined
                    ></v-text-field>
                  </v-col>

                  <v-col sm="5">
                    <v-select
                      :items="secrets"
                      item-text="name"
                      item-value="name"
                      v-model="env.fromSecret"
                      label="Select secret"
                      no-data-text="No secrets created yet"
                      dense
                      outlined
                    ></v-select>
                  </v-col>
                  <v-col sm="1">
                    <div class="text-center">
                      <v-btn
                        fab
                        icon
                        small
                        @click="deleteSecretEnvVar(application, i)"
                      >
                        <v-icon class="red--text">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </template>
    

              <v-row align="center" dense>
                <v-col class="d-flex" md="12" sm="12" xs="12">
                  <v-btn
                    outlined
                    class="secondary--text pr-2"
                    :disabled="loading"
                    :loading="loading"
                    small
                    @click="addSecretEnvVar(application)"
                  >
                    <v-icon small class="mr-1">add</v-icon>
                    <span class="mr-1 pt-1 pb-1">Add Secret Env</span>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <p class="mt-1 secondary--text">
                    Set an environment variable from Synpse secret
                  </p>
                </v-col>
              </v-row>

              <v-row align="center" dense>
                <span>
                  GPU ({{ application.gpu ? 'enabled' : 'disabled' }})
                </span>
                <v-spacer></v-spacer>
                <v-switch
                  v-model="application.gpu"                
                ></v-switch>
              </v-row>

              <v-row align="center" dense>
                <span>
                  Deployment ({{ schedulingSelector ? 'select by labels' : 'specify directly' }})
                </span>
                <v-spacer></v-spacer>
                <v-switch
                  v-model="schedulingSelector"                
                ></v-switch>
              </v-row>

              <!-- Select by labels -->
              <v-row v-if="schedulingSelector" align="center" dense>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field 
                    required 
                    label="Key"
                    outlined
                    v-model="application.selector.key"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    required
                    label="Value"
                    outlined
                    v-model="application.selector.value"
                  />
                </v-col>
              </v-row>

              <!-- Select directly -->
              <v-row v-else align="center" dense>
                <v-col cols="12" sm="12" md="12">
                  <v-select
                    no-data-text="No devices added to your project"
                    v-model="application.selector.devices"
                    :items="devices"
                    item-text="name"
                    item-value="id"
                    chips
                    label="Select one or more devices to deploy this application to..."
                    multiple
                    small
                    outlined
                  ></v-select>
                </v-col>
              </v-row>

              <page-subtitle style="padding-left: 0; padding-bottom: 4px; margin-top: 20px">
                <span>For advanced settings or multi-container applications, use yaml option.</span>              
              </page-subtitle>
              <page-subtitle v-if="application.gpu" style="padding-left: 0; padding-bottom: 4px; margin-top: 20px">
                <span>
                  For GPU CUDA setup refer to 
                  <a href="https://docs.nvidia.com/datacenter/cloud-native/container-toolkit/latest/install-guide.html" class="ml-1" target="_blank">Nvidia documentation</a>.
                </span>              
              </page-subtitle>
            </v-card-text>
          </v-form>
        </v-tab-item>

        <v-tab-item value="tab-yaml">
          <v-card-text>
            <page-subtitle style="padding-left: 0">
              <span>View all configuration options in the </span><a href="https://docs.synpse.net/synpse-core/applications/app-specification" class="ml-1" target="_blank"> docs</a>
            </page-subtitle>
            <v-row align="center" dense>
              <v-col class="d-flex" md="12" sm="12" xs="12">

                <codemirror
                  v-model="applicationYaml"
                  :options="cmOptions"
                ></codemirror>
              
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions v-if="error || yamlError">
            <!-- <v-alert v-if="error" dense outlined type="error">
              {{ error }}
            </v-alert> -->
            <v-alert v-if="yamlError" dense outlined type="error">
              {{ yamlError }}
            </v-alert>
          </v-card-actions>
        </v-tab-item>
      </v-tabs-items>



      <v-card-text>      
        <div v-show="error">
          <v-alert type="error" outlined>
            {{ error }}
          </v-alert>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn text v-on:click.native="dialog = false" class="primary--text">Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn text :disabled="loading || (tab === 'tab-form' && !valid)" :loading="loading" v-on:click.native.stop="createApplication()" class="primary--text">Create</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="stylus">
  .addbtn
    margin: 0px
    top: auto
    right: 20px
    bottom: 20px
    left: auto
    position: fixed
    z-index: 1
</style>

<style >
.CodeMirror {
  border: 1px solid #eee;
  height: auto;
}
</style>

<script>
import PageSubtitle from '../PageSubtitle.vue'

import YAML from 'yaml';
import getRandomName from './namesgenerator'

// Editor
import { codemirror } from 'vue-codemirror'

// require styles
import 'codemirror/lib/codemirror.css'

// language js
import 'codemirror/mode/yaml/yaml.js'

// theme css
import 'codemirror/theme/base16-dark.css'


export default {
    components: {
      PageSubtitle,
      codemirror
    },
    data () {
      return {
        dialog: false,
        tab: 'tab-form',
        schedulingSelector: false,
        overrideNamespace: '', // Override current namespace
        application: {
          name: getRandomName(),
          gpu: false,
          image: '',
          command: '',
          args: '',
          ports: '',
          volume: '',
          env: [],
          secretEnv: [],
          selector: {
            key: '',
            value: '',
            devices: [] // when selecting directly
          },
        },
        valid: false,
        nameRules: [
          v => !!v || 'Name is required',
          v => v.length <= 32 || 'Name must be less than 32 characters',
          v => v.length >= 1 || 'Name must be at least 1 character',
        ],
        imageRules: [
          v => !!v || 'Docker container image is required',
          v => v.length <= 256 || 'Image must be less than 256 characters',
          v => v.length >= 1 || 'Image name must be at least 1 character',
        ],
        envRules: [
          v => !!v || 'Env var is required',
          v => v.length <= 250 || 'Env var must be less than 250 characters',
        ],
        volumeRules: [        
          v => v.length <= 250 || 'Volume mount must be less than 250 characters',
          // Volume should be of format such as /data/foo:/data where ':' is required and
          // both host and container paths are required
          v => {
            if (v.length == 0) return true
            return v.split(':').length == 2 || 'Volume mount should be of format /host/path:/container/path'
          },
          // Validate that both host and container paths are provided and start with '/'
          v => {
            if (v.length == 0) return true
            return v.split(':')[0].startsWith('/') && v.split(':')[1].startsWith('/') || 'Both host and container paths should start with /'
          },
        ],
        applicationYaml: `name: hello-synpse # <- application name
scheduling:
  type: AllDevices # <- deploys on all project devices
  selectors: {}
spec:
  containers:
    - name: hello
      image: quay.io/synpse/hello-synpse-go:latest # <- Docker container image
      ports:
        - 8080:8080 # Specify host_port:container_port to expose your application
      env:
        - name: ENV_VARIABLE_NAME
          value: environment_variable_value
      volumes:
        - /data:/data # Specify host_dir:container_dir to mount for persistence
        `,
        yamlError: null,
        cmOptions: {
          // codemirror options
          tabSize: 4,
          mode: 'text/x-yaml',
          theme: 'base16-dark',
          lineNumbers: true,
          line: true
        }
      }
    },

    props: {
      selectedNamespaceId: {
        type: String,
        required: false
      }
    },

    watch: {
      dialog: function (val) {
        if (val) {
          // If namespace is not selected and there's only one
          // in the project, select it
          if (!this.selectedNamespaceId && this.namespaces.length == 1) {
            this.overrideNamespace = this.namespaces[0].id
          }
        } else {
          this.resetForm()
        }
      }
    },

    computed: {
      loading() {
        return this.$store.state.application.loading
      },
      error() {
        return this.$store.state.application.error
      },
      createdApplication() {
        return this.$store.state.application.createdApplication
      },
      namespaces() {
        return this.$store.state.namespace.namespaces
      },
      // Computed value to check when we have the namespace ready
      // for creation
      namespaceSelection() {
        return (!this.selectedNamespaceId && this.overrideNamespace === '') && (this.namespaces.length > 0)
      },
      secrets() {
        return this.$store.state.secret.secrets
      },
      projectId() {
        return this.$route.params.project
      },
      project() {
        return this.$store.state.project.project
      },
      devices() {
        return this.$store.state.device.devices
      },
    },

    methods: {
      createApplication () {
        if (this.tab == 'tab-form') {
          this.createApplicationFromConfig()
        } else {
          this.createApplicationFromYaml()
        }
      },
      createApplicationFromYaml() {
        this.yamlError = null
        let application = {}
        try {
          application = YAML.parse(this.applicationYaml)
        }
        catch (err) {
          this.yamlError = err
          return
        }

        if (!application.name) {
          this.yamlError = 'Application name is required'
          return
        }

        if (!application.spec) {
          this.yamlError = 'Application spec is required'
          return
        }

        if (!application.spec.containers) {
          this.yamlError = 'Application spec.containers is required'
          return
        }

        if (!application.spec.containers[0].image) {
          this.yamlError = 'Application spec.containers.image is required'
          return
        }

        if (!application.scheduling) {
          this.yamlError = 'Application scheduling is required'
          return
        }

        // Setting details
        application.projectId = this.projectId
        application.namespaceId = this.selectedNamespaceId
        if (this.overrideNamespace !== '') {
          application.namespaceId = this.overrideNamespace
        }

        this.$store.dispatch('CreateApplication', application).then(() => {
          if (this.error === null) {
            // All good
            this.$store.dispatch('Notify', `Application '${application.name}' created`)
            // Change route here to the newly created application
            this.$router.push(
              {
                name: 'applicationDetails',
                params: { project: this.projectId, namespace: application.namespaceId, application: this.createdApplication.id }
              })

            return
          }
          // Showing error
        })
      },
      async createApplicationFromConfig () {

        this.$refs.form.validate()

        if (!this.valid) {
          return
        }

        let payload = {
          name: this.application.name.toString(),
          scheduling: {
            type: 'Conditional',
          },
          spec: {
            containers: [{
              name: this.application.name.toString(),
              image: this.application.image,
              env: [],
            }]
          }
        }
        // Adding optional ones
        if (this.application.command.length != '') {
          payload.spec.containers[0].command = this.application.command.toString()
        }

        if (this.application.gpu) {
          payload.spec.containers[0].gpus = 'all'
        }

        if (this.application.args.length != '') {
          payload.spec.containers[0].args = this.application.args.toString().split(' ')
        }

        if (this.application.volume.length != '') {
          payload.spec.containers[0].volumes = [this.application.volume]
        }

        if (this.application.ports.length != '') {
          // Splitting comma separated ports
          
          let ports = this.application.ports.split(',')
          // Convert ports into pairs, for example 8000 becomes 8000:8000
          ports = ports.map(port => {
            // if port is already in format 8000:8000, don't change it
            if (port.includes(':')) {
              return port
            }
            // Otherwise, add the same port for host and container
            return port + ':' + port
          })

          payload.spec.containers[0].ports = ports
        }

        if (this.application.env.length != 0) {
          payload.spec.containers[0].env = this.application.env
        }

        if (this.application.secretEnv.length != 0) {
          payload.spec.containers[0].env.push(...this.application.secretEnv)
        }

        
        if (this.schedulingSelector) {          
          payload.scheduling.selectors = {
            [this.application.selector.key]: this.application.selector.value
          }
        } else if (this.application.selector.devices.length > 0) {
          // Generating
          payload.scheduling.devices = this.application.selector.devices
        }

        // Setting details
        payload.projectId = this.projectId
        payload.namespaceId = this.selectedNamespaceId

        if (this.overrideNamespace !== '') {
          payload.namespaceId = this.overrideNamespace
        }

        await this.$store.dispatch('CreateApplication', payload)

        if (this.error != null) {
          // got error, don't reset form, let user
          // fix the mistake :S
          return
        }

        // Labeling devices
        // if (!this.schedulingSelector && this.application.selector.devices.length > 0) {
        //   // Label devices
        //   await this.labelDevices(this.createdApplication.id)
        // }

        this.$store.dispatch('Notify', `Application '${payload.name}' created`)
        // Change route here to the newly created application
        this.$router.push(
          {
            name: 'applicationDetails',
            params: { project: this.projectId, namespace: payload.namespaceId, application: this.createdApplication.id }
          })
        this.resetForm()
      },
      addEnvVar(application) {
        application.env.push({
          'name': '',
          'value': '',
        })
      },
      deleteEnvVar(application, index) {
        application.env.splice(index, 1)
      },

      addSecretEnvVar(application) {
        application.secretEnv.push({
          'name': '',
          'fromSecret': '',
        })
      },
      deleteSecretEnvVar(application, index) {
        application.secretEnv.splice(index, 1)
      },

      resetForm: function () {
        // closing dialog
        this.dialog = false

        // resetting form
        this.overrideNamespace = ''
        this.application.name = ''
        this.application.description = ''
        this.application.image = 'ghcr.io/synpse-hq/hello-synpse-go'
        this.application.command = ''
        this.application.entrypoint = ''
        this.application.port = '8080'
        this.application.gpu = false
        this.application.env = []
        this.application.secretEnv = []
      }
    }
  }
</script>
